import { useMemo } from "react";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import { get } from "lodash";

export interface OrganizationSetupProgress {
  completedSteps: number;
  totalSteps: number;
  isLoaded: boolean;
}

interface ProgressData {
  currentSubStep: number;
  totalSubSteps: number;
  formData: any;
  status: 'draft' | 'submitted' | 'completed';
  lastSaved: number;
}

export const useOrganizationSetupProgress = (
  charityId: string,
  progressData: ProgressData | null,
  dataLoaded: boolean
): OrganizationSetupProgress => {
  return useMemo(() => {
    const totalSteps = 4;

    if (!dataLoaded) {
      return {
        completedSteps: 0,
        totalSteps,
        isLoaded: false,
      };
    }

    let completedSteps = 0;

    if (!progressData) {
      completedSteps = 0;
    } else if (progressData.status === 'completed') {
      completedSteps = totalSteps;
    } else if (progressData.status === 'submitted') {
      completedSteps = totalSteps;
    } else if (progressData.status === 'draft') {
      completedSteps = Math.min(progressData.currentSubStep + 1, totalSteps);
    }

    return {
      completedSteps,
      totalSteps,
      isLoaded: true,
    };
  }, [progressData, dataLoaded]);
};

const withOrganizationSetupProgress = (WrappedComponent: React.ComponentType<any>) => {
  const mapStateToProps = (state: any) => {
    const { user, firebase } = state;
    const charityId = user.currentCharity?.id || "";

    const dataLoaded =
      charityId &&
      isLoaded(firebase.data[charityId]) &&
      isLoaded(get(firebase, `data.${charityId}.charitySetup.organization`));

    const progressData: ProgressData | null = dataLoaded
      ? get(firebase, `data.${charityId}.charitySetup.organization`, null)
      : null;

    return {
      charityId,
      organizationProgressData: progressData,
      organizationDataLoaded: dataLoaded,
    };
  };

  return connect(mapStateToProps)(
    firebaseConnect((props: any, store: any) => {
      const charityId = store.getState().user.currentCharity?.id;
      return charityId 
        ? [`${charityId}/charitySetup/organization`] 
        : [];
    })(WrappedComponent)
  );
};

export { withOrganizationSetupProgress };